<template>
  <div class="space-y-6">
    <header class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-white">Product Catalog</h1>
        <p class="text-sm text-slate-400 mt-1">
          Manage product tags that equipment types can attach to.
        </p>
      </div>
      <button class="btn-primary" @click="openCreate">Add Product</button>
    </header>

    <section class="panel-neo panel-neo--glassier p-6 rounded-2xl">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <label class="text-xs uppercase tracking-[0.2em] text-slate-400">Search</label>
          <input
            v-model="search"
            type="text"
            placeholder="Filter by name or code"
            class="input-dark w-64"
          />
        </div>
        <div class="flex items-center gap-3 text-xs text-slate-400">
          <span class="inline-flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-emerald-400"></span> Active
          </span>
          <span class="inline-flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-rose-400"></span> Inactive
          </span>
        </div>
      </div>

      <div v-if="loading" class="py-24 text-center text-slate-400">Loading products…</div>
      <div v-else-if="error" class="py-24 text-center text-rose-400">{{ error }}</div>
      <div v-else>
        <div v-if="!filteredProducts.length" class="py-24 text-center text-slate-400">
          No products found.
        </div>
        <ul v-else class="space-y-3">
          <li
            v-for="product in filteredProducts"
            :key="product.id"
            class="flex items-center justify-between rounded-xl border border-slate-700/60 bg-slate-900/60 px-4 py-3"
          >
            <div class="flex items-center gap-4">
              <span
                class="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
                :class="
                  product.isActive
                    ? 'bg-emerald-500/20 text-emerald-200'
                    : 'bg-rose-500/20 text-rose-200'
                "
              >
                {{ product.code.slice(0, 2).toUpperCase() }}
              </span>
              <div>
                <p class="text-sm font-medium text-white">{{ product.name }}</p>
                <div class="text-xs text-slate-400 flex items-center gap-2">
                  <span>Code: {{ product.code }}</span>
                  <span class="text-slate-700">•</span>
                  <span>{{ product.description || 'No description' }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button class="btn-secondary" @click="beginEdit(product)">Edit</button>
              <button class="btn-secondary" @click="toggleActive(product)">
                {{ product.isActive ? 'Deactivate' : 'Activate' }}
              </button>
              <button class="btn-danger" @click="confirmDelete(product)">Delete</button>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <ProductDialog
      :open="dialogOpen"
      :mode="dialogMode"
      :product="activeProduct"
      :saving="saving"
      @close="closeDialog"
      @submit="handleSubmit"
    />

    <ConfirmDialog
      :open="confirmOpen"
      :message="confirmMessage"
      :loading="deleting"
      @close="confirmOpen = false"
      @confirm="performDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useInventoryStore } from '@/stores/inventory'
import {
  listEquipmentProducts,
  createEquipmentProduct,
  updateEquipmentProduct,
  deleteEquipmentProduct,
} from '@/lib/inventoryApi'
import type {
  EquipmentProduct,
  EquipmentProductCreateInput,
  EquipmentProductUpdateInput,
} from '@/types/inventory'
import ProductDialog from '@/components/admin/ProductDialog.vue'
import ConfirmDialog from '@/components/admin/ConfirmDialog.vue'
import { createLogger } from '@/lib/logger'

const log = createLogger('AdminProducts')

const inventory = useInventoryStore()
const products = ref<EquipmentProduct[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const search = ref('')

const dialogOpen = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const activeProduct = ref<EquipmentProduct | null>(null)
const saving = ref(false)

const confirmOpen = ref(false)
const confirmMessage = ref('')
const productToDelete = ref<EquipmentProduct | null>(null)
const deleting = ref(false)

const filteredProducts = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return products.value
  return products.value.filter((product) => {
    return (
      product.name.toLowerCase().includes(term) ||
      product.code.toLowerCase().includes(term) ||
      (product.description ?? '').toLowerCase().includes(term)
    )
  })
})

const toast = (message: string, type: 'success' | 'error' = 'success') => {
  try {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type, message } }))
  } catch {}
}

async function loadProducts(force = false) {
  if (loading.value) return
  loading.value = true
  error.value = null
  try {
    if (force) {
      await inventory.loadProducts(true)
    } else if (!inventory.products.length) {
      await inventory.loadProducts()
    }
    products.value = await listEquipmentProducts()
  } catch (err) {
    log.error('loadProducts failed', err)
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function openCreate() {
  dialogMode.value = 'create'
  activeProduct.value = null
  dialogOpen.value = true
}

function beginEdit(product: EquipmentProduct) {
  dialogMode.value = 'edit'
  activeProduct.value = product
  dialogOpen.value = true
}

function closeDialog() {
  dialogOpen.value = false
  activeProduct.value = null
}

async function handleSubmit(payload: EquipmentProductCreateInput | EquipmentProductUpdateInput) {
  if (saving.value) return
  saving.value = true
  try {
    if (dialogMode.value === 'create') {
      await createEquipmentProduct(payload as EquipmentProductCreateInput)
      toast('Product created', 'success')
    } else if (activeProduct.value) {
      await updateEquipmentProduct(activeProduct.value.id, payload as EquipmentProductUpdateInput)
      toast('Product updated', 'success')
    }
    await loadProducts(true)
    dialogOpen.value = false
  } catch (err) {
    log.error('submit failed', err)
    toast(err instanceof Error ? err.message : 'Failed to save product', 'error')
  } finally {
    saving.value = false
  }
}

function confirmDelete(product: EquipmentProduct) {
  confirmMessage.value = `Delete product “${product.name}”?`
  productToDelete.value = product
  confirmOpen.value = true
}

async function performDelete() {
  if (!productToDelete.value) return
  deleting.value = true
  try {
    await deleteEquipmentProduct(productToDelete.value.id)
    confirmOpen.value = false
    productToDelete.value = null
    await loadProducts(true)
  } catch (err) {
    log.error('delete failed', err)
    toast(err instanceof Error ? err.message : 'Failed to delete product', 'error')
  } finally {
    deleting.value = false
  }
}

async function toggleActive(product: EquipmentProduct) {
  const nextState = !product.isActive
  try {
    await updateEquipmentProduct(product.id, { isActive: nextState })
    toast(nextState ? 'Product activated' : 'Product deactivated', 'success')
    await loadProducts(true)
  } catch (err) {
    log.error('toggleActive failed', err)
    toast(err instanceof Error ? err.message : 'Failed to update product', 'error')
  }
}

onMounted(() => {
  loadProducts()
})
</script>
